// The Run Result of a run as orchestration serves it (P-095 slice 8 part 1,
// agion/Agion#2939; challenge record Amendments S08A-1 and S08A-2).
//
// GET /v1/executions/{id} carries `result` and metadata.follow_up, and the run
// metrics carry the completed-with-limitations counts, only with
// MISSION_RUN_RESULT_ENABLED on. Without them every screen keeps today's
// reading. The browser maps each server value to its text and computes nothing:
// the result, the follow-up decision and its reason, and the counts are the
// server's answers.
import { unifiedApi } from '@/lib/api-client'
import { nextPageOffset } from './paging'

export type RunResult = 'completed' | 'completed_with_limitations' | 'failed' | 'cancelled'

const RUN_RESULTS: readonly string[] = ['completed', 'completed_with_limitations', 'failed', 'cancelled']

/**
 * One code of the run's end record (orchestration StoredCode), in the order
 * the server stored it: the registry's effect and default cause, and whether
 * the run recovered from it.
 */
export interface RunEndCode {
  code: string
  effect: string
  cause: string
  resolved: boolean
  /** The detail the code's source named beside it (agion/Agion#3457), e.g. host_store_full; absent when the server stored none. */
  reason?: string
}

export interface RunResultReading {
  result: RunResult
  /** The server's reason no follow-up started; absent when one may start or none was recorded. */
  followUpReason?: string
  /** The run end record's codes; absent when the server stored none. */
  codes?: RunEndCode[]
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined
}

/** The stored codes the server sent, each as served; an element that is not a code is left out. */
function runEndCodesOf(value: unknown): RunEndCode[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((element) => {
    const c = record(element)
    if (typeof c?.['code'] !== 'string') return []
    return [{
      code: c['code'],
      effect: typeof c['effect'] === 'string' ? c['effect'] : '',
      cause: typeof c['cause'] === 'string' ? c['cause'] : '',
      resolved: c['resolved'] === true,
      ...(typeof c['reason'] === 'string' && c['reason'] ? { reason: c['reason'] } : {}),
    }]
  })
}

/**
 * Who started a run, as its run read names it (agion/Agion#3444). The screens
 * word it and never show the user id.
 */
export interface RunStarter {
  initiatorType?: string
  initiatedByUserId?: string
}

/** One run read: its Run Result, null without one, and its starter, null when the read names neither field. */
export interface RunRead {
  result: RunResultReading | null
  starter: RunStarter | null
}

/** The run's result, follow-up reason and codes from its run API record; null when the server sent no result. */
export function runResultOf(detail: unknown): RunResultReading | null {
  const run = record(detail)
  const result = run?.['result']
  if (typeof result !== 'string' || !RUN_RESULTS.includes(result)) return null
  const metadata = record(run?.['metadata'])
  const followUp = record(metadata?.['followUp'] ?? metadata?.['follow_up'])
  const reason = followUp?.['decision'] === 'suppress' ? followUp['reason'] : undefined
  const codes = runEndCodesOf(run?.['codes'])
  return {
    result: result as RunResult,
    ...(typeof reason === 'string' && reason ? { followUpReason: reason } : {}),
    ...(codes.length > 0 ? { codes } : {}),
  }
}

/** The run's starter from its run API record; null when the record names neither field. */
export function runStarterOf(detail: unknown): RunStarter | null {
  const run = record(detail)
  const initiatorType = run?.['initiatorType']
  const initiatedByUserId = run?.['initiatedByUserId']
  const type = typeof initiatorType === 'string' ? initiatorType : ''
  const userId = typeof initiatedByUserId === 'string' ? initiatedByUserId : ''
  if (!type && !userId) return null
  return { ...(type ? { initiatorType: type } : {}), ...(userId ? { initiatedByUserId: userId } : {}) }
}

/** One run's read (GET /v1/executions/{id}); null when the run could not be read. */
export async function getRunRead(executionId: string): Promise<RunRead | null> {
  try {
    const detail = await unifiedApi.get<unknown>(`executions/${encodeURIComponent(executionId)}`)
    return { result: runResultOf(detail), starter: runStarterOf(detail) }
  } catch {
    return null
  }
}

/** One run's Run Result; null when the server sent none (switch off) or the run could not be read. */
export async function getRunResult(executionId: string): Promise<RunResultReading | null> {
  return (await getRunRead(executionId))?.result ?? null
}

const RUN_LIST_PAGE = 100

/** The Run Results the run list gave, and whether the list was read until every named run was found or it ended. */
export interface RunResultsReading {
  results: Map<string, RunResultReading>
  readInFull: boolean
}

/**
 * The Run Result of the named runs of one mission, read from the mission's
 * run list (GET /v1/executions?mission_id=, newest first, 100 a page) until
 * every named run is found or the list ends (agion/Agion#2834), however many
 * pages that takes (agion/Agion#3391). A run the list does not hold, or holds
 * without a result (switch off, or not ended), has no entry. A read that
 * fails, or a page that repeats the page before it, ends the reading with what
 * it has and readInFull false.
 */
export async function getRunResults(missionId: string, executionIds: readonly string[]): Promise<RunResultsReading> {
  const wanted = new Set(executionIds)
  const results = new Map<string, RunResultReading>()
  let previousFirstId: unknown
  // Each read starts one page on (nextPageOffset); an empty page ends the list.
  for (let offset = 0; wanted.size > 0;) {
    let runs: unknown[]
    let answeredLimit: unknown
    try {
      const response = await unifiedApi.get<{ executions?: unknown[]; limit?: number }>('executions', {
        mission_id: missionId,
        limit: RUN_LIST_PAGE,
        offset,
      })
      runs = Array.isArray(response?.executions) ? response.executions : []
      answeredLimit = response?.limit
    } catch {
      return { results, readInFull: false }
    }
    if (runs.length === 0) break
    const firstId = record(runs[0])?.['id']
    if (offset > 0 && firstId !== undefined && firstId === previousFirstId) return { results, readInFull: false }
    previousFirstId = firstId
    for (const run of runs) {
      const id = record(run)?.['id']
      if (typeof id !== 'string' || !wanted.has(id)) continue
      wanted.delete(id)
      const reading = runResultOf(run)
      if (reading) results.set(id, reading)
    }
    offset = nextPageOffset(offset, answeredLimit, RUN_LIST_PAGE)
  }
  return { results, readInFull: true }
}

/** One run of the compliance export: the server's result (null when it serves none) and how many of its events failed. */
export interface ExportedRun {
  result: RunResult | null
  failedEvents: number
}

/**
 * The compliance export's run counts (agion/Agion#2834). A run with the
 * server's result counts by it: completed and completed with limitations
 * are successful, failed is failed, cancelled is stopped. A run the server
 * gave no result (MISSION_RUN_RESULT_ENABLED off) counts as before: failed
 * when any of its events failed.
 */
export function exportRunCounts(runs: readonly ExportedRun[]) {
  const count = (result: RunResult) => runs.filter((run) => run.result === result).length
  const unread = runs.filter((run) => run.result === null)
  return {
    successfulExecutions: count('completed') + count('completed_with_limitations') + unread.filter((run) => run.failedEvents === 0).length,
    completedWithLimitationsExecutions: count('completed_with_limitations'),
    failedExecutions: count('failed') + unread.filter((run) => run.failedEvents > 0).length,
    cancelledExecutions: count('cancelled'),
    executionsWithoutResult: unread.length,
  }
}

const FOLLOW_UP = 'pages:audit.missionDetail.followUp'
const FAILURE_CODE_PREFIX = 'failure_code:'

const FOLLOW_UP_KEYS: Record<string, string> = {
  result_failed: `${FOLLOW_UP}.resultFailed`,
  result_cancelled: `${FOLLOW_UP}.resultCancelled`,
  result_limited: `${FOLLOW_UP}.resultLimited`,
  chain_depth_limit: `${FOLLOW_UP}.chainDepthLimit`,
}

/**
 * The text key for the server's reason no follow-up started. Every
 * failure_code:<CODE> reason reads as one sentence, so no raw code reaches the
 * screen (S08A-2); an unknown reason shows nothing.
 */
export function followUpReasonKey(reason: string | undefined): string | undefined {
  if (!reason) return undefined
  return FOLLOW_UP_KEYS[reason] ?? (reason.startsWith(FAILURE_CODE_PREFIX) ? `${FOLLOW_UP}.failureCode` : undefined)
}

/**
 * The server's completed-with-limitations count, present only when the run
 * metrics read the Run Result (switch on). Its presence is how a page knows the
 * server reads the Run Result.
 */
export function limitedCountOf(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}
